import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useAuth } from '../auth/AuthContext';
import { bookingApi, type MonthlySalesResponse, type BookingEntity } from '../api/BookingApi';
import { usersApi } from '../api/UsersApi';
import { customersApi } from '../api/CustomersApi';
import { formatPrice } from '../utils/formatPrice';
import { cn } from '../utils/cn';
import { generateDashboardPdf } from '../utils/generateDashboardPdf';
import { Button } from '../components/ui/Button';
import { useI18n } from '../i18n/I18nContext';

const COLORS = ['#dc2626', '#f59e0b', '#10b981', '#f43f5e', '#64748b', '#8b5cf6'];

type TooltipRow = { label: string; value: string; color?: string };

function ChartTooltip({ title, rows }: { title?: string; rows: TooltipRow[] }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/95">
      {title && (
        <div className="mb-1.5 text-xs font-semibold text-slate-900 dark:text-slate-100">{title}</div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-xs whitespace-nowrap">
          {r.color && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.color }} />}
          <span className="text-slate-500 dark:text-slate-400">{r.label}</span>
          <span className="ml-3 font-semibold text-slate-900 dark:text-slate-100">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function icon(d: string) {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const icons = {
  calendar: icon('M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5'),
  users: icon('M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z'),
  customers: icon('M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z'),
  currency: icon('M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'),
  profit: icon('M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941'),
  expense: icon('M2.25 6 9 12.75l4.286-4.286a11.948 11.948 0 0 1 4.306 6.43l.776 2.898m0 0 3.182-5.511m-3.182 5.51-5.511-3.181'),
};

const statusTone: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300',
  cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
  no_show: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const statusLabelKey: Record<string, string> = {
  pending: 'common.status.pending',
  confirmed: 'common.status.confirmed',
  completed: 'common.status.completed',
  cancelled: 'common.status.cancelled',
  no_show: 'common.status.noShow',
};

function formatDate(iso: string, t: (key: string) => string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const month = t(`common.monthsShort.${d.getMonth() + 1}`);
  return `${dd} ${month} ${d.getFullYear()}, ${hh}:${mm}`;
}

function deltaPercent(current: number, previous: number): { value: string; positive: boolean } | null {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return { value: '+100%', positive: true };
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const sign = pct >= 0 ? '+' : '';
  return { value: `${sign}${pct.toFixed(1)}%`, positive: pct >= 0 };
}

function StatCard({ title, value, hint, delta, iconNode, color }: {
  title: string; value: string; hint?: string;
  delta?: { value: string; positive: boolean } | null;
  iconNode: React.ReactNode; color: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide sm:text-xs dark:text-slate-400">{title}</div>
          <div className="mt-1.5 break-words text-lg font-bold text-slate-900 sm:mt-2 sm:text-2xl dark:text-slate-100">{value}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            {delta && (
              <span className={cn(
                'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                delta.positive ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
              )}>
                {delta.value}
              </span>
            )}
            {hint && <span className="text-xs text-slate-500 dark:text-slate-400">{hint}</span>}
          </div>
        </div>
        <div className={cn('hidden h-11 w-11 place-items-center rounded-xl shrink-0 sm:grid', color)}>
          {iconNode}
        </div>
      </div>
    </div>
  );
}

function MonthPicker({ month, year, onChange }: {
  month: number; year: number;
  onChange: (m: number, y: number) => void;
}) {
  const { t } = useI18n();
  function prev() {
    if (month === 1) onChange(12, year - 1);
    else onChange(month - 1, year);
  }
  function next() {
    const now = new Date();
    const nextM = month === 12 ? 1 : month + 1;
    const nextY = month === 12 ? year + 1 : year;
    if (nextY > now.getFullYear() || (nextY === now.getFullYear() && nextM > now.getMonth() + 1)) return;
    onChange(nextM, nextY);
  }
  const now = new Date();
  const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();

  return (
    <div className="inline-flex flex-1 items-center gap-1 rounded-xl border border-slate-200 bg-white shadow-sm sm:flex-none dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        onClick={prev}
        className="rounded-l-xl px-3 py-2 text-slate-600 hover:bg-slate-50 transition dark:text-slate-300 dark:hover:bg-slate-800"
        aria-label={t('common.aria.previousMonth')}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
      </button>
      <div className="flex-1 px-2 py-2 text-center text-sm font-semibold text-slate-900 sm:min-w-[140px] sm:px-3 dark:text-slate-100">
        {t(`common.months.${month}`)} {year}
      </div>
      <button
        type="button"
        onClick={next}
        disabled={isCurrentMonth}
        className="rounded-r-xl px-3 py-2 text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed dark:text-slate-300 dark:hover:bg-slate-800"
        aria-label={t('common.aria.nextMonth')}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [sales, setSales] = useState<MonthlySalesResponse | null>(null);
  const [recentBookings, setRecentBookings] = useState<BookingEntity[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalServices, setTotalServices] = useState(0);

  const topServicesRef = useRef<HTMLDivElement>(null);
  const paymentPieRef = useRef<HTMLDivElement>(null);
  const staffPerformanceRef = useRef<HTMLDivElement>(null);
  const paymentBreakdownRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async (m: number, y: number) => {
    setLoading(true);
    setError(null);
    try {
      const [salesRes, recentRes, usersRes, custRes, svcRes] = await Promise.all([
        bookingApi.monthlySales(m, y),
        bookingApi.recentBookings({ limit: 8, status: 'pending,confirmed,completed' }),
        usersApi.list({ limit: 1, status: 'active' }),
        customersApi.list({ limit: 1 }),
        bookingApi.listServices(),
      ]);
      setSales(salesRes.data);
      setRecentBookings(recentRes.data.data);
      setTotalUsers(usersRes.data.total);
      setTotalCustomers(custRes.data.total);
      setTotalServices(svcRes.data.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData(month, year);
  }, [month, year, loadData]);

  function handleMonthChange(m: number, y: number) {
    setMonth(m);
    setYear(y);
  }

  async function handleDownloadPdf() {
    if (!sales) return;
    setPdfLoading(true);
    try {
      await generateDashboardPdf({
        sales,
        totalUsers,
        totalCustomers,
        totalServices,
        month,
        year,
        chartRefs: {
          topServices: topServicesRef.current,
          paymentPie: paymentPieRef.current,
          staffPerformance: staffPerformanceRef.current,
          paymentBreakdown: paymentBreakdownRef.current,
        },
        t,
      });
    } catch (err) {
      console.error('Failed to generate PDF', err);
    } finally {
      setPdfLoading(false);
    }
  }

  const s = sales?.summary;
  const prev = sales?.previousMonth;
  const bd = sales?.breakdowns;

  /* ---- Pie chart from payment method breakdown ---- */
  const paymentPieData = useMemo(() => {
    if (!bd?.byPaymentMethod?.length) return [];
    return bd.byPaymentMethod.map((p) => ({
      name: p.key || t('common.unknown'),
      value: p.amount,
    }));
  }, [bd, t]);

  /* ---- Top services bar chart ---- */
  const topServicesData = useMemo(() => {
    if (!bd?.topServices?.length) return [];
    return bd.topServices.slice(0, 5).map((s) => ({
      name: s.label && s.label.length > 18 ? s.label.slice(0, 18) + '…' : (s.label || s.key),
      count: s.count,
      revenue: s.amount,
    }));
  }, [bd]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-red-200 border-t-red-600 dark:border-red-950 dark:border-t-red-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">{error}</div>
        <button className="text-sm font-medium text-red-600 hover:underline" onClick={() => loadData(month, year)}>{t('common.retry')}</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {user?.name ? t('dashboard.welcomeBackWithName', { name: user.name }) : t('dashboard.welcomeBack')}
          </div>
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('dashboard.overviewFor', { month: t(`common.months.${month}`), year })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <MonthPicker month={month} year={year} onChange={handleMonthChange} />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={pdfLoading || !sales}
          >
            {pdfLoading ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('dashboard.generatingPdf')}
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                {t('dashboard.downloadPdf')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.stats.revenue')}
          value={formatPrice(s?.revenue ?? 0)}
          delta={prev ? deltaPercent(s?.revenue ?? 0, prev.revenue) : null}
          hint={t('dashboard.stats.vsLastMonth')}
          iconNode={icons.currency}
          color="bg-green-100 text-green-600"
        />
        <StatCard
          title={t('dashboard.stats.bookings')}
          value={String(s?.bookingCount ?? 0)}
          delta={prev ? deltaPercent(s?.bookingCount ?? 0, prev.bookingCount) : null}
          hint={t('dashboard.stats.completed')}
          iconNode={icons.calendar}
          color="bg-red-100 text-red-600"
        />
        <StatCard
          title={t('dashboard.stats.netProfit')}
          value={formatPrice(s?.netProfit ?? 0)}
          delta={prev ? deltaPercent(s?.netProfit ?? 0, prev.netProfit) : null}
          hint={s?.margin != null ? t('dashboard.stats.marginSuffix', { value: s.margin.toFixed(1) }) : undefined}
          iconNode={icons.profit}
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard
          title={t('dashboard.stats.expenses')}
          value={formatPrice(s?.expenses ?? 0)}
          delta={prev ? deltaPercent(s?.expenses ?? 0, prev.expenses) : null}
          hint={t('dashboard.stats.itemsSuffix', { count: s?.expenseCount ?? 0 })}
          iconNode={icons.expense}
          color="bg-rose-100 text-rose-600"
        />
      </div>

      {/* Secondary stat row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.stats.avgBookingValue')}
          value={formatPrice(s?.averageBookingValue ?? 0)}
          iconNode={icons.currency}
          color="bg-amber-100 text-amber-600"
        />
        <StatCard
          title={t('dashboard.stats.commission')}
          value={formatPrice(s?.commission ?? 0)}
          hint={t('dashboard.stats.entriesSuffix', { count: s?.commissionCount ?? 0 })}
          iconNode={icons.users}
          color="bg-violet-100 text-violet-600"
        />
        <StatCard
          title={t('dashboard.stats.customers')}
          value={totalCustomers.toLocaleString()}
          iconNode={icons.customers}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          title={t('dashboard.stats.staff')}
          value={String(totalUsers)}
          hint={t('dashboard.stats.servicesSuffix', { count: totalServices })}
          iconNode={icons.users}
          color="bg-slate-100 text-slate-600"
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Top Services */}
        <div ref={topServicesRef} className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.charts.topServices')}</div>
          {topServicesData.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">{t('dashboard.charts.noDataThisMonth')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topServicesData} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                <XAxis
                  type="number"
                  fontSize={12}
                  tick={{ fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                />
                <YAxis type="category" dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} axisLine={false} tickLine={false} width={120} />
                <Tooltip
                  cursor={{ fill: 'rgba(148, 163, 184, 0.12)', radius: 6 }}
                  wrapperStyle={{ outline: 'none' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const row = payload[0].payload as { count?: number; revenue?: number };
                    return (
                      <ChartTooltip
                        title={String(label)}
                        rows={[
                          { label: t('dashboard.charts.revenue'), value: formatPrice(Number(row.revenue ?? 0)), color: '#dc2626' },
                          { label: t('dashboard.stats.bookings'), value: String(row.count ?? 0) },
                        ]}
                      />
                    );
                  }}
                />
                <Bar
                  dataKey="revenue"
                  fill="#dc2626"
                  radius={[0, 6, 6, 0]}
                  maxBarSize={28}
                  name="revenue"
                  activeBar={{ fill: '#b91c1c' }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Payment Method Pie */}
        <div ref={paymentPieRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.charts.revenueByPayment')}</div>
          {paymentPieData.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">{t('common.noData')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={paymentPieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                  activeShape={{ style: { filter: 'brightness(1.1)' } }}
                >
                  {paymentPieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />
                  ))}
                </Pie>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} formatter={(v) => String(v).replace(/_/g, ' ')} />
                <Tooltip
                  wrapperStyle={{ outline: 'none' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const item = payload[0];
                    const idx = paymentPieData.findIndex((d) => d.name === item.name);
                    return (
                      <ChartTooltip
                        title={String(item.name ?? '').replace(/_/g, ' ')}
                        rows={[
                          {
                            label: t('dashboard.recentBookings.amount'),
                            value: formatPrice(Number(item.value ?? 0)),
                            color: COLORS[(idx < 0 ? 0 : idx) % COLORS.length],
                          },
                        ]}
                      />
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Staff performance */}
        <div ref={staffPerformanceRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.charts.staffPerformance')}</div>
          {!bd?.byStaff?.length ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">{t('common.noData')}</div>
          ) : (
            <div className="space-y-3 max-h-[280px] overflow-y-auto">
              {bd.byStaff.map((staff) => {
                const maxAmount = bd.byStaff[0]?.amount || 1;
                const pct = Math.round((staff.amount / maxAmount) * 100);
                return (
                  <div key={staff.key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700 dark:text-slate-300">{staff.label || staff.key}</span>
                      <span className="text-slate-500 dark:text-slate-400">
                        {t('dashboard.charts.bookingsCountAndAmount', { count: staff.count, amount: formatPrice(staff.amount) })}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden dark:bg-slate-800">
                      <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payment status + expense breakdown */}
        <div ref={paymentBreakdownRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.charts.paymentBreakdown')}</div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Revenue payment */}
            <div>
              <div className="mb-2 text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">{t('dashboard.charts.revenue')}</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="text-slate-700 dark:text-slate-300">{t('dashboard.charts.paid')}</span>
                  </span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{formatPrice(s?.paidRevenue ?? 0)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-rose-500" />
                    <span className="text-slate-700 dark:text-slate-300">{t('dashboard.charts.unpaid')}</span>
                  </span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{formatPrice(s?.unpaidRevenue ?? 0)}</span>
                </div>
              </div>
              {(s?.revenue ?? 0) > 0 && (
                <div className="mt-2 h-2 rounded-full bg-rose-200 overflow-hidden dark:bg-rose-950/50">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${((s?.paidRevenue ?? 0) / (s?.revenue ?? 1)) * 100}%` }}
                  />
                </div>
              )}
            </div>

            {/* Expense payment methods */}
            <div>
              <div className="mb-2 text-xs font-semibold text-slate-500 uppercase dark:text-slate-400">{t('dashboard.charts.expensesLabel')}</div>
              {!bd?.byExpensePaymentMethod?.length ? (
                <div className="text-xs text-slate-400 dark:text-slate-500">{t('dashboard.charts.noExpenses')}</div>
              ) : (
                <div className="space-y-2">
                  {bd.byExpensePaymentMethod.map((ep) => (
                    <div key={ep.key} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700 capitalize dark:text-slate-300">{ep.key || t('common.unknown')}</span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">{formatPrice(ep.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.recentBookings.title')}</div>
          <button
            className="text-xs font-medium text-red-600 hover:text-red-700"
            onClick={() => navigate('/app/booking/bookings')}
          >
            {t('common.viewAll')}
          </button>
        </div>
        {recentBookings.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">{t('dashboard.recentBookings.empty')}</div>
        ) : (
          <>
          {/* Mobile card list */}
          <div className="space-y-2 md:hidden">
            {recentBookings.map((b) => (
              <div
                key={b.serviceBookingId}
                className="cursor-pointer rounded-xl border border-slate-200 p-3 active:bg-slate-50 dark:border-slate-700 dark:active:bg-slate-800"
                onClick={() => navigate('/app/booking/bookings')}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                      {b.customer?.name ?? t('dashboard.recentBookings.walkIn')}
                    </div>
                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {b.service?.title ?? t('common.notAvailable')}
                    </div>
                  </div>
                  <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize', statusTone[b.status] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}>
                    {statusLabelKey[b.status] ? t(statusLabelKey[b.status]) : b.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-500 dark:text-slate-400">{formatDate(b.reservationDateTime, t)}</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">{formatPrice(b.totalPrice)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-[11px] text-slate-500 border-b border-slate-100 dark:text-slate-400 dark:border-slate-800">
                  <th className="pb-2 pr-3 font-semibold">{t('dashboard.recentBookings.customer')}</th>
                  <th className="pb-2 pr-3 font-semibold">{t('dashboard.recentBookings.service')}</th>
                  <th className="pb-2 pr-3 font-semibold">{t('dashboard.recentBookings.reservationDate')}</th>
                  <th className="pb-2 pr-3 font-semibold text-right">{t('dashboard.recentBookings.amount')}</th>
                  <th className="pb-2 font-semibold">{t('dashboard.recentBookings.status')}</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((b) => (
                  <tr key={b.serviceBookingId} className="border-b border-slate-50 last:border-0 hover:bg-slate-50 transition cursor-pointer dark:border-slate-800 dark:hover:bg-slate-800/60" onClick={() => navigate('/app/booking/bookings')}>
                    <td className="py-2.5 pr-3 font-medium text-slate-900 max-w-[140px] truncate dark:text-slate-100">
                      {b.customer?.name ?? t('dashboard.recentBookings.walkIn')}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600 max-w-[140px] truncate dark:text-slate-400">
                      {b.service?.title ?? t('common.notAvailable')}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap dark:text-slate-400">
                      {formatDate(b.reservationDateTime, t)}
                    </td>
                    <td className="py-2.5 pr-3 text-right text-slate-700 whitespace-nowrap dark:text-slate-300">
                      {formatPrice(b.totalPrice)}
                    </td>
                    <td className="py-2.5">
                      <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize', statusTone[b.status] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}>
                        {statusLabelKey[b.status] ? t(statusLabelKey[b.status]) : b.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
